import { useId, useRef, useState } from 'react';
import { AVATAR_IDS, avatarPngUrl, avatarVideoUrl, avatarWebpUrl } from '@redpandev/shared';

export type AvatarPickerProps = {
  /** Id sélectionné (`null` = pas encore d'avatar → mascotte en fallback,
   *  mais la mascotte n'est PAS un choix de la galerie). */
  value: string | null;
  onChange: (avatarId: string) => void;
  /** Nom accessible du radiogroup. */
  label?: string;
  className?: string;
};

/**
 * Galerie d'avatars (spec 07 §2) : grille de ronds, sémantique RADIOGROUP
 * accessible (radios natifs masqués visuellement → clavier natif Tab/flèches,
 * état annoncé aux lecteurs d'écran). Sélection = anneau panda + coche ;
 * focus clavier relayé sur le rond (`peer-focus-visible`). Chaque option est
 * rendue en `<picture>` webp+png — le navigateur choisit (spec 07 §2.3).
 *
 * Ici — et SEULEMENT ici — la vidéo ne se lance qu'au SURVOL (ou focus clavier) :
 * afficher 36 animations en continu serait lourd et illisible (spec 07 §2.3).
 */
export function AvatarPicker({
  value,
  onChange,
  label = "Galerie d'avatars",
  className,
}: AvatarPickerProps) {
  // Un name unique par instance : plusieurs pickers peuvent cohabiter (signup + compte).
  const groupName = useId();

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={`grid grid-cols-6 justify-items-center gap-2 sm:grid-cols-9 ${className ?? ''}`}
    >
      {AVATAR_IDS.map((id) => (
        <AvatarOption
          key={id}
          id={id}
          name={groupName}
          optionLabel={`Avatar ${id}`}
          checked={value === id}
          onSelect={() => onChange(id)}
        />
      ))}
    </div>
  );
}

/**
 * Une option : radio natif masqué (sr-only) + rond visuel qui reflète coche/focus.
 * La vignette `<picture>` est toujours affichée ; la vidéo (plus lourde) ne se
 * charge et ne joue qu'au survol / focus clavier, en fondu par-dessus.
 */
function AvatarOption({
  id,
  name,
  optionLabel,
  checked,
  onSelect,
}: {
  id: string;
  name: string;
  optionLabel: string;
  checked: boolean;
  onSelect: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [active, setActive] = useState(false);

  const play = () => {
    setActive(true);
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    // Politique autoplay ou environnement sans média (jsdom) : on garde la vignette.
    try {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch {
      /* pas de lecture → vignette conservée */
    }
  };
  const stop = () => {
    setActive(false);
    const v = videoRef.current;
    if (!v) return;
    try {
      v.pause();
      v.currentTime = 0;
    } catch {
      /* no-op */
    }
  };

  return (
    <label
      className="relative block cursor-pointer"
      onMouseEnter={play}
      onMouseLeave={stop}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onSelect}
        onFocus={play}
        onBlur={stop}
        aria-label={optionLabel}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className={
          // Cible ≥ 44px, rond, léger pop au survol (coupé si reduced-motion).
          'relative flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border bg-ink-700 ' +
          'transition-[box-shadow,border-color] duration-fast ease-fun ' +
          'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ring ' +
          'motion-safe:hover:scale-105 motion-safe:transition-transform ' +
          (checked
            ? 'border-panda-500 shadow-glow-panda ring-2 ring-panda-500 ring-offset-2 ring-offset-app'
            : 'border-ink-600/60 hover:border-panda-500/60')
        }
      >
        <picture className="contents">
          <source srcSet={avatarWebpUrl(id)} type="image/webp" />
          <img
            src={avatarPngUrl(id)}
            alt=""
            width={48}
            height={48}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </picture>
        <video
          ref={videoRef}
          src={avatarVideoUrl(id)}
          muted
          loop
          playsInline
          preload="none"
          aria-hidden="true"
          tabIndex={-1}
          className={
            'absolute inset-0 h-full w-full object-cover transition-opacity duration-fast ' +
            (active ? 'opacity-100' : 'opacity-0')
          }
        />
      </span>
      {/* Coche de sélection : repère non chromatique en plus de l'anneau. */}
      {checked && (
        <span
          aria-hidden="true"
          className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-panda-500 text-xs font-bold text-ink-950 shadow-card"
        >
          ✓
        </span>
      )}
    </label>
  );
}
